import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import useDebounced from '../../hooks/useDebounced'
import useEntityList from '../../hooks/pagination/useEntityList'
import PaletteInput from './PaletteInput'
import PaletteFacets from './PaletteFacets'
import PaletteResults from './PaletteResults'
import './SearchPalette.css'

// Notion-style command palette, fully generic. A feature supplies:
//   fetchPage({ cursor, params, limit }) -> { items, nextCursor, hasMore }
//     params = { q, facets } where facets is the raw facet-values object
//   facets       config array (see PaletteFacets)
//   renderItem   (item, { query }) -> node
//   onSelect     (item) -> void  (palette closes after)
//
// It owns the query (debounced), facet values, keyboard navigation and infinite
// scroll; pagination/data live in useEntityList so any list endpoint plugs in.
export default function SearchPalette({
  fetchPage,
  facets = [],
  renderItem,
  onSelect,
  close,
  placeholder = 'Search…',
  emptyLabel = 'No matches.',
  pageSize = 30,
  initialFacetValues = {},
}) {
  const [query, setQuery] = useState('')
  const [facetValues, setFacetValues] = useState(initialFacetValues)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  const debouncedQuery = useDebounced(query, 180)
  const params = useMemo(
    () => ({ q: debouncedQuery.trim(), facets: facetValues }),
    [debouncedQuery, facetValues]
  )

  const { items, loading, loadingMore, hasMore, error, loadMore } = useEntityList({
    fetchPage,
    params,
    pageSize,
  })

  // Reset the highlighted row whenever the query/facets change.
  useEffect(() => setActiveIndex(0), [params])

  // Keep the active row scrolled into view during keyboard navigation.
  useEffect(() => {
    const rows = listRef.current?.querySelectorAll('.palette-result')
    rows?.[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const changeFacet = useCallback((key, value) => {
    setFacetValues((prev) => ({ ...prev, [key]: value }))
  }, [])

  const select = useCallback(
    (item) => {
      onSelect?.(item)
      close?.()
    },
    [onSelect, close]
  )

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, items.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const item = items[activeIndex]
      if (item) select(item)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      close?.()
    }
  }

  return createPortal(
    <div className="palette-overlay" onMouseDown={close}>
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <PaletteInput
          value={query}
          onChange={setQuery}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          inputRef={inputRef}
        />
        <PaletteFacets facets={facets} values={facetValues} onChange={changeFacet} />
        <PaletteResults
          items={items}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          error={error}
          query={debouncedQuery}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          onSelect={select}
          renderItem={renderItem}
          loadMore={loadMore}
          listRef={listRef}
          emptyLabel={emptyLabel}
        />
      </div>
    </div>,
    document.body
  )
}
